import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Bookmark, BookmarkPlus, ChevronDown, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * CRM-R05 · Barra de vistas guardadas de filtros (por usuario y pantalla).
 * Props:
 *   screen          clave de la pantalla ("mercado" | "licitaciones")
 *   currentFilters  objeto de filtros activos a guardar
 *   onApply(filters) callback al cargar una vista
 */
export const SavedViewsBar = ({ screen, currentFilters, onApply }) => {
  const [views, setViews] = useState([]);
  const [open, setOpen] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/crm/saved-views`, { params: { screen } });
      setViews(r.data || []);
    } catch { /* silencioso */ }
  }, [screen]);

  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    const nombre = window.prompt("Nombre de la vista guardada:");
    if (!nombre || !nombre.trim()) return;
    try {
      await axios.post(`${API}/crm/saved-views`, { screen, nombre: nombre.trim(), filters: currentFilters || {} });
      toast.success(`Vista "${nombre.trim()}" guardada`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la vista");
    }
  };

  const apply = (v) => { onApply?.(v.filters || {}); setOpen(false); toast.info(`Vista "${v.nombre}" aplicada`); };

  const remove = async (e, v) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/crm/saved-views/${v.id}`);
      reload();
    } catch { toast.error("No se pudo borrar"); }
  };

  return (
    <div className="relative inline-flex items-center gap-1" data-testid="saved-views-bar">
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="saved-views-toggle"
          className="text-[11px] font-semibold px-2.5 py-1 rounded-md border bg-white text-gray-600 border-gray-300 hover:border-indigo-400 hover:text-indigo-700 inline-flex items-center gap-1.5">
          <Bookmark size={12} /> Vistas {views.length > 0 && <span className="text-[9px] bg-indigo-100 text-indigo-700 px-1.5 rounded-full">{views.length}</span>}
          <ChevronDown size={12} />
        </button>
        {open && (
          <div className="absolute left-0 top-8 z-50 w-64 bg-white border border-gray-200 rounded-xl shadow-xl p-2" data-testid="saved-views-panel">
            {views.length === 0 && <p className="text-[11px] text-gray-400 px-2 py-2">Aún no hay vistas guardadas.</p>}
            {views.map((v) => (
              <div key={v.id} className="flex items-center justify-between text-[11px] hover:bg-indigo-50 rounded px-2 py-1 cursor-pointer" onClick={() => apply(v)} data-testid={`saved-view-${v.id}`}>
                <span className="flex items-center gap-1.5 text-gray-700 font-medium truncate flex-1"><Check size={11} className="text-indigo-500" /> {v.nombre}</span>
                <button onClick={(e) => remove(e, v)} className="text-rose-300 hover:text-rose-600" title="Borrar vista" data-testid={`saved-view-delete-${v.id}`}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={save}
        data-testid="saved-views-save"
        title="Guardar los filtros actuales como vista"
        className="text-[11px] font-semibold px-2.5 py-1 rounded-md border bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50 inline-flex items-center gap-1.5">
        <BookmarkPlus size={12} /> Guardar vista
      </button>
    </div>
  );
};

export default SavedViewsBar;
